import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { FastField, Field, FormikProps, getIn } from "formik";
import * as React from "react";
import { FormField } from "../FormField";
import { FieldOption } from "../FieldOption";
import FormikRadioGroup from "../FormikRadioGroup";
import { GetLabelColor, maybePadRight } from "../Functions";

export function MultipleChoiceField({
  formik,
  field,
}: {
  formik: FormikProps<Record<string, unknown>>;
  field: FormField;
}) {
  const theme = useTheme();

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    choice: FieldOption,
  ) => {
    let valueArray = [...getIn(formik.values, field.id)];
    if (event.target.checked) {
      valueArray.push(choice.value);
    } else {
      valueArray.splice(valueArray.indexOf(choice.value), 1);
    }
    formik.setFieldValue(field.id, valueArray);
  };

  return (
    <Grid size={{ xs: 12, sm: field.columns || 6 }} key={field.id}>
      {field.fixedLabel && (
        <Typography sx={{ color: GetLabelColor(formik, field, theme) }}>
          {field.text}
        </Typography>
      )}
      <Box sx={{ display: "flex" }}>
        <FormControl component="fieldset" variant="standard">
          {field.text && <FormLabel component="legend">{field.text}</FormLabel>}
          <FormGroup>
            {field.choices!.map((choice, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    checked={getIn(formik.values, field.id).includes(choice.value)}
                    onChange={(event) => handleChange(event, choice)}
                    name={choice.id}
                  />
                }
                label={choice.text}
              />
            ))}
          </FormGroup>
        </FormControl>
      </Box>
    </Grid>
  );
}

export function RadioButtonsField({
  formik,
  field,
}: {
  formik: FormikProps<Record<string, unknown>>;
  field: FormField;
}) {
  const Component =
    field.visibilityFunction || field.visibilityConditions ? Field : FastField;

  return (
    <React.Fragment key={field.id}>
      <Grid size={{ xs: 12, sm: field.columns || 6 }} key={field.id}>
        <Component
          name={field.id}
          label={!field.fixedLabel && field.text}
          tooltip={field.tooltip}
          options={field.choices}
          component={FormikRadioGroup}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            formik.setFieldValue(field.id, e.target.value);
          }}
        />
      </Grid>
      {maybePadRight(field)}
    </React.Fragment>
  );
}

export function CheckboxField({
  formik,
  field,
}: {
  formik: FormikProps<Record<string, unknown>>;
  field: FormField;
}) {
  return (
    <Grid size={{ xs: 12, sm: field.columns || 6 }} key={field.id}>
      <FormControl
        error={getIn(formik.touched, field.id) && Boolean(getIn(formik.errors, field.id))}
        component="fieldset"
      >
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                name={field.id}
                id={field.id}
                checked={
                  getIn(formik.values, field.id) !== "" &&
                  getIn(formik.values, field.id) === true
                }
                onChange={(e) => {
                  formik.setFieldValue(field.id, e.target.checked);
                }}
                onBlur={formik.handleBlur}
                slotProps={{ input: { "aria-label": "controlled" } }}
              />
            }
            label={field.text}
          />
        </FormGroup>
        {getIn(formik.touched, field.id) && getIn(formik.errors, field.id) && (
          <FormHelperText error>
            {getIn(formik.errors, field.id) as string}
          </FormHelperText>
        )}
      </FormControl>
    </Grid>
  );
}
